import { defineComponent as _defineComponent } from 'vue'
import { isNil, uniqueId } from '@/utils/misc';
import { DateTimeFormatOptions } from 'vue-i18n';
import { DateTimeGranularity } from '@/components/common/dateTimeRangePicker/DateTimeGranularity';
import { useI18n } from '@/composables/i18n';
import { computed, nextTick, reactive, ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { exclusiveMaxDate, required, exclusiveMinDate } from '@/utils/validation';


export default /*#__PURE__*/_defineComponent({
  __name: 'DateTimeRangePicker',
  props: {
    value: null,
    granularity: { default: DateTimeGranularity.SECONDS }
  },
  emits: ["input"],
  setup(__props: any, { emit }: { emit: ((e: 'input', value: [Date, Date]) => void), expose: any, slots: any, attrs: any }) {

const props = __props as { value?: [Date, Date], granularity: DateTimeGranularity };

const MINUTES_FIELD_SELECTOR = '.timepicker .control:nth-child(3) select';





const { t } = useI18n();

const canCloseRangeDropdown = ref(true);
const startDateTimePickerRef = ref<any>();
const endDateTimePickerRef = ref<any>();
const dropdownRef = ref<any>();

const componentId = ref(uniqueId());

const state = reactive({
  startDateTime: props.value?.[0] ?? null,
  endDateTime: props.value?.[1] ?? null,
});

const validations = computed(() => ({
  startDateTime: {
    required,
    maxDate: exclusiveMaxDate(state.endDateTime),
  },
  endDateTime: {
    required,
    minDate: exclusiveMinDate(state.startDateTime),
  },
}));

const v$ = useVuelidate(validations, state);

function updateStartAndEndFields() {
  state.startDateTime = props.value?.[0] ?? null;
  state.endDateTime = props.value?.[1] ?? null;
}

function updateMinutesFieldDisabledStatus(dateTimePickerComponent: any) {
  const selectElement = dateTimePickerComponent.$el.querySelector(
    MINUTES_FIELD_SELECTOR,
  );
  if (hasMinutes.value) {
    selectElement.removeAttribute('disabled');
  } else {
    selectElement.setAttribute('disabled', 'disabled');
  }
}

function formatDateTime(date: Date) {
  const formatOptions: DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',

    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };
  return new Intl.DateTimeFormat(undefined, formatOptions).format(date);
}

function onAcceptClicked() {
  emit('input', [state.startDateTime!, state.endDateTime!]);
  dropdownRef.value?.toggle();
}

function onDropdownOpenStatusChanged(isOpen: boolean) {
  if (!isOpen) {
    // Reset the fields back to their initial values. Otherwise, if they've
    // been changed, they'll preserve the same values when the dropdown is
    // opened again.
    updateStartAndEndFields();
    v$.value.$reset();
  }
}

function onDateTimePickerDropdownOpenStatusChanged(isOpen: boolean) {
  // To avoid the range dropdown being closed when clicking outside a
  // date-time picker modal, we disable closing it while a selector dropdown
  // is open.
  canCloseRangeDropdown.value = !isOpen;
}

const formattedValue = computed<string | undefined>(() => {
  if (isNil(props.value)) {
    return undefined;
  }
  return props.value
    .map((date) =>
      isNil(date) ? t('date_time_range_picker.unset') : formatDateTime(date),
    )
    .join(' - ');
});

const granularityCssClasses = computed<Record<string, boolean>>(() => ({
  'has-time': hasTime.value,
  'has-calendar': hasCalendar.value,
}));

const hasCalendar = computed<boolean>(() =>
  [
    DateTimeGranularity.HOURS,
    DateTimeGranularity.SECONDS,
    DateTimeGranularity.DAYS,
    DateTimeGranularity.MONTHS,
  ].includes(props.granularity),
);

const hasTime = computed<boolean>(() =>
  [DateTimeGranularity.HOURS, DateTimeGranularity.SECONDS].includes(
    props.granularity,
  ),
);

const hasMinutes = computed<boolean>(() => hasSeconds.value);

const hasSeconds = computed<boolean>(
  () => props.granularity === DateTimeGranularity.SECONDS,
);

// immediate so we don't have to update from mounted
watch(
  () => props.value,
  () => {
    updateStartAndEndFields();
  },
  { immediate: true },
);

watch(
  () => props.granularity,
  () => {
    nextTick(() => {
      updateMinutesFieldDisabledStatus(startDateTimePickerRef.value);
      updateMinutesFieldDisabledStatus(endDateTimePickerRef.value);
    });
  },
  { immediate: true },
);

return { __sfc: true,MINUTES_FIELD_SELECTOR, props, emit, t, canCloseRangeDropdown, startDateTimePickerRef, endDateTimePickerRef, dropdownRef, componentId, state, validations, v$, updateStartAndEndFields, updateMinutesFieldDisabledStatus, formatDateTime, onAcceptClicked, onDropdownOpenStatusChanged, onDateTimePickerDropdownOpenStatusChanged, formattedValue, granularityCssClasses, hasCalendar, hasTime, hasMinutes, hasSeconds }
}

})